import dynamic from 'next/dynamic';
import { useTranslation, TFunction } from 'next-i18next';
import { VisuallyHidden } from '@chakra-ui/react';
import { ListAgencies as Props } from '@/types';
import TeaserList from '../TeaserList';
import ContactInfoStack, {
  getContactInfoStackItems,
} from '@/components/common/ContactInfoStack';
import { publicContainerAttachmentUrl, urls } from '@/lib/urls';
import formatAddress from '@/lib/formatAddress';

const Image = dynamic(() => import('../Image'));

const treeIconSx = {
  ':before': {
    content: "''",
    background: "url('/trees.png') no-repeat center/contain",
    width: '40px',
    mr: '.25em',
    flexShrink: 0,
  },
};

export const mapAgenciesToTeasers = (
  agencies: Props['agencies'],
  t: TFunction,
) => {
  const readMore = t('read_more', 'Lue lisää');

  return agencies?.map(
    ({
      name,
      id,
      address,
      postal_code,
      municipality,
      email,
      phone_number: phone,
      logo_attachment,
    }) => {
      const logo =
        logo_attachment && publicContainerAttachmentUrl(logo_attachment);
      const heading = logo
        ? {
            title: (
              <div>
                <Image
                  src={logo}
                  width="250"
                  height="44"
                  alt={name}
                  objectFit="contain"
                  objectPosition="0 0"
                  noRounding
                />
                <VisuallyHidden as="h2" aria-hidden="true">
                  {name}
                </VisuallyHidden>
              </div>
            ),
          }
        : { title: name, sx: treeIconSx, display: 'flex' };
      return {
        heading,
        link: { url: urls.public.agencies.get({ name, id }), title: readMore },
        id,
        innerProps: { height: '100%' },
        children: (
          <ContactInfoStack
            height="100%"
            items={getContactInfoStackItems({
              address: formatAddress({
                street: address,
                postalCode: postal_code,
                city: municipality,
              }),
              email,
              phone,
            })}
            displayLinks
          />
        ),
      };
    },
  );
};

const ListAgencies = ({ title, agencies, ...rest }: Props) => {
  const { t } = useTranslation('frontoffice');

  return (
    <TeaserList
      entries={mapAgenciesToTeasers(agencies, t)}
      heading={title || null}
      {...rest}
    />
  );
};

export default ListAgencies;
